import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import StatCard from "../components/StatCard";
import RevenueChart from "../components/Charts/RevenueChart";
import PieChartBox from "../components/Charts/PieChartBox";
import Dashboard1 from "./Dashboard1";
import { getAnalytics, updateAdminProfile, getMe } from "../services/adminService";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import ChartFilterBar, { ChartPeriod } from "../components/Charts/ChartFilterBar";
import {
  Sparkles,
  Activity,
  ChevronRight,
  Phone,
  Mail,
  User,
  X,
  MessageSquare,
  IndianRupee,
  CalendarCheck,
  GraduationCap,
  Users,
  TrendingUp,
  TrendingDown,
  Receipt,
  CheckCircle2,
  XCircle,
  Briefcase,
  RotateCcw,
  UserCheck,
  FileBarChart2,
  Shield,
} from "lucide-react";
import { getUserRole, getUserDepartment } from "../utils/auth";
import {
  pageContainer, titleStyle, subtitleStyle, cardStyle,
  statsGrid, labelStyle, valueStyle, sectionTitle,
  primaryBtn, selectStyle,
} from "../styles/theme";

const Dashboard = ({ timeRange = "30d" }: { isDark?: boolean; timeRange?: string }) => {
  const [stats, setStats] = useState<any>({});
  const [loading, setLoading] = useState(true);
  const [revPeriod, setRevPeriod] = useState<ChartPeriod>("month");
  const [piePeriod, setPiePeriod] = useState<ChartPeriod>("all");
  const [currentUser, setCurrentUser] = useState<any>(null);
  const [agentStatus, setAgentStatus] = useState<string>("ONLINE");
  const [selectedDispatchBooking, setSelectedDispatchBooking] = useState<any>(null);
  const [copiedText, setCopiedText] = useState<string | null>(null);
  const navigate = useNavigate();

  const handleCopy = (text: string, label: string) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    setCopiedText(label);
    setTimeout(() => setCopiedText(null), 2000);
  };

  useEffect(() => {
    const loadDashboardData = async () => {
      setLoading(true);
      let activeDept = getUserDepartment() || "";
      try {
        const res = await getMe();
        if (res?.data) {
          setCurrentUser(res.data);
          setAgentStatus(res.data.availabilityStatus || "ONLINE");
          if (res.data.department) activeDept = res.data.department;
          else if (res.data.role) activeDept = res.data.role;
        }
      } catch (err) {
        console.error("Failed to load user info", err);
      }

      if (!activeDept) activeDept = getUserRole() || "OPERATIONS";
      const data = await getAnalytics(timeRange, activeDept);
      if (data) setStats(data);
      setLoading(false);
    };

    loadDashboardData();
  }, [timeRange]);

  const handleStatusChange = async (newStatus: string) => {
    try {
      setAgentStatus(newStatus);
      await updateAdminProfile({ availabilityStatus: newStatus });
      // Refresh analytics
      const data = await getAnalytics(timeRange);
      if (data) setStats(data);
    } catch (err) {
      console.error("Failed to update status:", err);
    }
  };

  if (loading) {
    return (
      <div style={{ padding: 24, textAlign: "center", color: "hsl(var(--muted-foreground))" }}>
        Loading dashboard analytics...
      </div>
    );
  }

  const userRole = (currentUser?.role || getUserRole() || "").toUpperCase();
  const userDept = (currentUser?.department || getUserDepartment() || stats?.department || userRole).toUpperCase();

  // ── 1. SUPPORT DASHBOARD ──────────────────────────────────────────
  if (userDept.includes("SUPPORT")) {
    const isAgent = stats.isAgent;

    const statusColors: Record<string, string> = {
      ONLINE: "#10b981",
      OFFLINE: "#ef4444",
      BREAK: "#f97316",
    };

    return (
      <div style={{ ...pageContainer, minHeight: "auto", padding: 0, display: "grid", gap: 28 }}>
        <section style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 16, borderBottom: "1px solid hsl(var(--border))", paddingBottom: 20 }}>
          <div>
            <h1 style={titleStyle}>
              {isAgent ? "Support Agent Dashboard" : "Support Dashboard"}
            </h1>
            <p style={subtitleStyle}>
              {isAgent
                ? "Track your assigned tickets, reply to customer support requests, and monitor your response times."
                : "Monitor and assign tickets, track SLA compliance, and view online support agents."}
            </p>
          </div>
          <Button onClick={() => navigate("/support")} style={primaryBtn}>
            {isAgent ? "My Tickets" : "Manage Tickets"}
          </Button>
        </section>

        {/* Agent Profile & Status Controller Elevation Card */}
        {isAgent && (
          <section style={{ ...cardStyle, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 20, background: "linear-gradient(135deg, hsl(var(--card)) 0%, hsl(var(--muted)/0.2) 100%)", borderColor: "hsl(var(--primary)/0.25)" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
              <div style={{ width: 54, height: 54, borderRadius: "50%", background: "hsl(var(--primary))", display: "flex", alignItems: "center", justifyContent: "center", color: "white", fontSize: 20, fontWeight: 800, boxShadow: "0 4px 12px hsl(var(--primary)/0.2)" }}>
                {currentUser?.name ? currentUser.name.split(" ").map((n: string) => n[0]).join("").toUpperCase().slice(0, 2) : "SA"}
              </div>
              <div>
                <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: "hsl(var(--foreground))" }}>
                  Welcome back, {currentUser?.name || "Support Agent"}!
                </h3>
                <p style={{ margin: "4px 0 0", fontSize: 12, color: "hsl(var(--muted-foreground))" }}>
                  Assigned role: Support Operations Agent &bull; {currentUser?.email}
                </p>
              </div>
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: 12, background: "hsl(var(--muted)/0.5)", padding: "8px 16px", borderRadius: 12, border: "1px solid hsl(var(--border))" }}>
              <span style={{ width: 10, height: 10, borderRadius: "50%", backgroundColor: statusColors[agentStatus] || "#cbd5e1", display: "inline-block", boxShadow: `0 0 10px ${statusColors[agentStatus] || "#cbd5e1"}` }} />
              <label style={{ fontSize: 13, fontWeight: 600, color: "hsl(var(--foreground))" }}>Status:</label>
              <select
                value={agentStatus}
                onChange={(e) => handleStatusChange(e.target.value)}
                style={{ ...selectStyle, padding: "4px 8px", border: "none", background: "transparent", fontWeight: 700, color: "hsl(var(--foreground))", cursor: "pointer", outline: "none" }}
              >
                <option value="ONLINE">ONLINE</option>
                <option value="BREAK">BREAK</option>
                <option value="OFFLINE">OFFLINE</option>
              </select>
            </div>
          </section>
        )}

        <section style={statsGrid}>
          <div style={cardStyle}>
            <div style={labelStyle}>{isAgent ? "My Assigned Tickets" : "Total Tickets"}</div>
            <div style={valueStyle}>{stats.totalTickets?.toLocaleString() || "0"}</div>
            <div style={{ fontSize: 11, color: "hsl(var(--muted-foreground))" }}>Total tickets assigned to queue</div>
          </div>
          <div style={cardStyle}>
            <div style={labelStyle}>{isAgent ? "My Open Tickets" : "Open Tickets"}</div>
            <div style={{ ...valueStyle, color: "hsl(var(--primary))" }}>{stats.openTickets?.toLocaleString() || "0"}</div>
            <div style={{ fontSize: 11, color: "#10b981", fontWeight: 600 }}>Active tickets needing attention</div>
          </div>
          <div style={cardStyle}>
            <div style={labelStyle}>{isAgent ? "My Resolved Tickets" : "Resolved Tickets"}</div>
            <div style={valueStyle}>{stats.resolvedTickets?.toLocaleString() || "0"}</div>
            <div style={{ fontSize: 11, color: "hsl(var(--muted-foreground))" }}>Closed tickets in current cycle</div>
          </div>
          <div style={cardStyle}>
            <div style={labelStyle}>{isAgent ? "My Avg Resolution" : "Avg Resolution Time"}</div>
            <div style={valueStyle}>{stats.avgResolutionTime} mins</div>
            <div style={{ fontSize: 11, color: "hsl(var(--muted-foreground))" }}>Target SLA: &lt; 15 mins</div>
          </div>
        </section>

        <section style={{ display: "grid", gridTemplateColumns: isAgent ? "repeat(auto-fit, minmax(320px, 1fr))" : "repeat(auto-fit, minmax(340px, 1fr))", gap: 24 }}>
          {/* Priority Breakdown */}
          <div style={cardStyle}>
            <h3 style={sectionTitle}>Ticket Priority Breakdown</h3>
            <div style={{ display: "grid", gap: 12 }}>
              {stats.priorityBreakdown?.map((p: any) => {
                const priorityColors: Record<string, string> = {
                  CRITICAL: "#ef4444",
                  HIGH: "#f97316",
                  MEDIUM: "#3b82f6",
                  LOW: "#10b981",
                };
                const color = priorityColors[p.priority?.toUpperCase()] || "hsl(var(--primary))";
                return (
                  <div key={p.priority} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 14px", background: "hsl(var(--muted)/0.25)", borderRadius: 10, borderLeft: `4px solid ${color}` }}>
                    <strong style={{ fontSize: 14, fontWeight: 700 }}>{p.priority}</strong>
                    <span style={{ fontSize: 13, color: "hsl(var(--foreground))", fontWeight: "700" }}>{p.count} tickets</span>
                  </div>
                );
              })}
              {(!stats.priorityBreakdown || stats.priorityBreakdown.length === 0) && (
                <div style={{ color: "hsl(var(--muted-foreground))", textAlign: "center", padding: 20 }}>No active tickets.</div>
              )}
            </div>
          </div>

          {/* SLA Performance Card or Support staff list */}
          {isAgent ? (
            <div style={cardStyle}>
              <h3 style={sectionTitle}>SLA Performance & Health</h3>
              <div style={{ display: "grid", gap: 16 }}>
                <div>
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, fontWeight: 600, marginBottom: 6 }}>
                    <span>First Response SLA Compliance</span>
                    <span style={{ color: "#10b981" }}>98.4%</span>
                  </div>
                  <div style={{ height: 8, background: "hsl(var(--muted))", borderRadius: 4, overflow: "hidden" }}>
                    <div style={{ width: "98.4%", height: "100%", background: "#10b981" }} />
                  </div>
                </div>

                <div>
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, fontWeight: 600, marginBottom: 6 }}>
                    <span>Resolution SLA Compliance</span>
                    <span style={{ color: "#10b981" }}>96.1%</span>
                  </div>
                  <div style={{ height: 8, background: "hsl(var(--muted))", borderRadius: 4, overflow: "hidden" }}>
                    <div style={{ width: "96.1%", height: "100%", background: "#10b981" }} />
                  </div>
                </div>

                <div style={{ marginTop: 10, padding: 14, background: "hsl(var(--primary)/0.05)", borderRadius: 12, border: "1px dashed hsl(var(--primary)/0.2)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <div>
                    <div style={{ fontSize: 11, color: "hsl(var(--muted-foreground))", fontWeight: 600 }}>Active Chat Capacity</div>
                    <div style={{ fontSize: 16, fontWeight: 800, color: "hsl(var(--foreground))", marginTop: 2 }}>3 Maximum Concurrency</div>
                  </div>
                  <span style={{ fontSize: 11, color: "hsl(var(--primary))", background: "hsl(var(--primary)/0.12)", padding: "2px 8px", borderRadius: 12, fontWeight: 700 }}>Auto-Assign On</span>
                </div>
              </div>
            </div>
          ) : (
            <div style={cardStyle}>
              <h3 style={sectionTitle}>Support Staff Directory</h3>
              <div style={{ display: "grid", gap: 12 }}>
                {stats.agents?.map((a: any) => (
                  <div key={a.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 12px", background: "hsl(var(--muted)/0.25)", borderRadius: 10 }}>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: 13 }}>{a.name}</div>
                      <div style={{ fontSize: 11, color: "hsl(var(--muted-foreground))" }}>{a.email}</div>
                    </div>
                    <span style={{ fontSize: 11, color: a.status === "OFFLINE" ? "#ef4444" : a.status === "BREAK" ? "#f97316" : "#10b981", background: a.status === "OFFLINE" ? "#fee2e2" : a.status === "BREAK" ? "#ffedd5" : "#d1fae5", padding: "2px 8px", borderRadius: 12, fontWeight: 700 }}>
                      {a.status}
                    </span>
                  </div>
                ))}
                {(!stats.agents || stats.agents.length === 0) && (
                  <div style={{ color: "hsl(var(--muted-foreground))", textAlign: "center", padding: 20 }}>No support staff configured.</div>
                )}
              </div>
            </div>
          )}
        </section>
      </div>
    );
  }

  // ── 2. FINANCE DASHBOARD ──────────────────────────────────────────
  if (userDept.includes("FINANCE")) {
    return (
      <div style={{ display: "grid", gap: 24 }}>
        <section style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 16 }}>
          <div>
            <h1 style={{ fontSize: "2rem", margin: 0 }}>Finance Dashboard</h1>
            <p style={{ margin: "8px 0 0", color: "hsl(var(--muted-foreground))" }}>
              Track revenue, process pending payout requests, and manage customer refunds.
            </p>
          </div>
          <Button onClick={() => navigate("/finance/payouts")}>Manage Payouts & Refunds</Button>
        </section>

        <section style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 20 }}>
          <StatCard title="Total Revenue" value={`₹${stats?.totalRevenue?.toLocaleString() || 0}`} trendUp />
          <StatCard title="Successful Payments" value={stats?.totalPayments?.toLocaleString() || "0"} />
          <StatCard title="Refunds Processed" value={stats?.totalRefunds?.toLocaleString() || "0"} />
          <StatCard title="Pending Payouts" value={stats?.pendingPayouts?.toLocaleString() || "0"} trend="Needs Approval" />
        </section>

        <section style={{ display: "grid", gridTemplateColumns: "1fr", gap: 24 }}>
          {/* Revenue Chart */}
          <Card>
            <CardHeader style={{ paddingBottom: 8 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <CardTitle>Daily Revenue Trend</CardTitle>
                <ChartFilterBar value={revPeriod} onChange={setRevPeriod} />
              </div>
            </CardHeader>
            <CardContent style={{ height: 320 }}>
              <RevenueChart period={revPeriod} showFilter={false} height={280} />
            </CardContent>
          </Card>
        </section>
      </div>
    );
  }

  // ── 3. OPERATIONS COMMAND CENTER ───────────────────────────────────
  if (userDept.includes("OPERATIONS") || userDept.includes("OPS")) {
    return (
      <div style={{ display: "grid", gap: 24, fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif" }}>
        {/* Operations Command Header */}
        <section style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 16,
          padding: "24px 28px",
          background: "linear-gradient(135deg, hsl(var(--card)) 0%, hsl(var(--muted)/0.3) 100%)",
          borderRadius: 16,
          border: "1px solid hsl(var(--border))",
          boxShadow: "0 4px 20px -2px rgba(0,0,0,0.05)"
        }}>
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
              <h1 style={{ fontSize: "1.4rem", fontWeight: 700, margin: 0, letterSpacing: "-0.01em", color: "hsl(var(--foreground))" }}>
                Operations Command Center
              </h1>
              <span style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                padding: "3px 8px",
                borderRadius: 16,
                fontSize: 11,
                fontWeight: 700,
                background: "rgba(16, 185, 129, 0.12)",
                color: "#10b981",
                border: "1px solid rgba(16, 185, 129, 0.25)"
              }}>
                <span style={{ width: 7, height: 7, borderRadius: "50%", background: "#10b981" }} />
                OPERATIONS LIVE
              </span>
            </div>
            <p style={{ margin: 0, color: "hsl(var(--muted-foreground))", fontSize: 13, lineHeight: 1.5 }}>
              Real-time scholar verification pipeline, live booking dispatchment, order assistance, and fulfillment SLA metrics.
            </p>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            <Button
              onClick={() => navigate("/scholars")}
              style={{ display: "flex", alignItems: "center", gap: 8, fontWeight: 600, background: "linear-gradient(135deg, #eab308, #ca8a04)", color: "#fff" }}
            >
              <Sparkles size={16} /> Scholar Applications ({stats?.pendingScholars || 0})
            </Button>
            <Button
              onClick={() => navigate("/bookings")}
              style={{ display: "flex", alignItems: "center", gap: 8, fontWeight: 600, background: "linear-gradient(135deg, #2563eb, #1d4ed8)", color: "#fff" }}
            >
              <Activity size={16} /> Booking Dispatch Center
            </Button>
          </div>
        </section>

        {/* Top 4 Operational Production Metrics */}
        <section style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 16 }}>
          <StatCard title="Scholar Verification Queue" value={stats?.pendingScholars?.toLocaleString() || "0"} trend="Pending Review" />
          <StatCard title="Active Live Dispatch" value={stats?.activeBookings?.toLocaleString() || "0"} trend="In-Transit / Active" trendUp />
          <StatCard title="Completed Services" value={stats?.completedBookings?.toLocaleString() || "0"} trend="Fulfilled Lifetime" trendUp />
          <StatCard title="Open Support Tickets" value={stats?.openTickets?.toLocaleString() || "0"} trend="Action Required" />
        </section>

        {/* Main 2-Column Operational Control Center */}
        <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1.6fr) minmax(0, 1fr)", gap: 24 }}>
          
          {/* Left Column: Scholar Onboarding & Booking Dispatch Stream */}
          <div style={{ display: "grid", gap: 24 }}>

            {/* 1. Scholar Onboarding & Document Verification Hub */}
            <Card style={{ borderRadius: 16, border: "1px solid hsl(var(--border))" }}>
              <CardHeader style={{ display: "flex", flexDirection: "row", alignItems: "center", justifyContent: "space-between", paddingBottom: 12 }}>
                <div>
                  <CardTitle style={{ fontSize: 18, fontWeight: 700, display: "flex", alignItems: "center", gap: 8, color: "hsl(var(--foreground))" }}>
                    <Sparkles size={20} style={{ color: "#eab308" }} />
                    Scholar Onboarding & Identity Verification Queue
                  </CardTitle>
                  <p style={{ margin: "4px 0 0", fontSize: 13, color: "hsl(var(--muted-foreground))" }}>
                    Review and verify scholar applicants, credentials, and religious qualifications before live dispatch access.
                  </p>
                </div>
                <Button variant="ghost" onClick={() => navigate("/scholars")} style={{ fontSize: 13 }}>
                  View All Scholars <ChevronRight size={14} />
                </Button>
              </CardHeader>
              <CardContent>
                <div style={{ display: "grid", gap: 12 }}>
                  {stats?.pendingApplications?.slice(0, 4).map((app: any) => (
                    <div
                      key={app.id}
                      style={{
                        padding: "14px 16px",
                        borderRadius: 12,
                        background: "hsl(var(--muted)/0.25)",
                        border: "1px solid hsl(var(--border)/0.6)",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center"
                      }}
                    >
                      <div>
                        <div style={{ fontWeight: 700, fontSize: 14, color: "hsl(var(--foreground))" }}>
                          {app.user?.name || "Scholar Applicant"}
                        </div>
                        <div style={{ fontSize: 12, color: "hsl(var(--muted-foreground))", marginTop: 2 }}>
                          {app.user?.email || app.user?.phone || "No contact info"} &bull; <strong style={{ color: "hsl(var(--primary))" }}>{app.religion || "General"}</strong>
                        </div>
                        <div style={{ fontSize: 11, color: "#eab308", marginTop: 3, fontWeight: 600 }}>
                          Status: PENDING REVIEW
                        </div>
                      </div>
                      <Button
                        onClick={() => navigate("/scholars")}
                        style={{ height: 32, fontSize: 12, fontWeight: 700, background: "#eab308", color: "#fff" }}
                      >
                        Review Application
                      </Button>
                    </div>
                  ))}
                  {(!stats?.pendingApplications || stats?.pendingApplications?.length === 0) && (
                    <div style={{ color: "hsl(var(--muted-foreground))", textAlign: "center", padding: 24, fontSize: 13, display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
                      <CheckCircle2 size={16} color="#10b981" /> Verification Queue Clear! All scholar applications are currently processed.
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* 2. Live Booking Dispatchment & Order Assist Hub */}
            <Card style={{ borderRadius: 16, border: "1px solid hsl(var(--border))" }}>
              <CardHeader style={{ display: "flex", flexDirection: "row", alignItems: "center", justifyContent: "space-between", paddingBottom: 12 }}>
                <div>
                  <CardTitle style={{ fontSize: 18, fontWeight: 700, display: "flex", alignItems: "center", gap: 8 }}>
                    <Activity size={20} style={{ color: "#3b82f6" }} />
                    Live Active Dispatch & Order Assist Hub
                  </CardTitle>
                  <p style={{ margin: "4px 0 0", fontSize: 13, color: "hsl(var(--muted-foreground))" }}>
                    Monitor live dispatches, scheduled orders, and assist customers or scholars in real time.
                  </p>
                </div>
                <Button variant="ghost" onClick={() => navigate("/bookings")} style={{ fontSize: 13 }}>
                  Dispatch Management <ChevronRight size={14} />
                </Button>
              </CardHeader>
              <CardContent>
                <div style={{ display: "grid", gap: 12 }}>
                  {stats?.recentLiveBookings?.map((b: any) => {
                    const shortId = b.id ? b.id.slice(0, 8).toUpperCase() : "N/A";
                    const statusColorMap: Record<string, { bg: string; text: string }> = {
                      PENDING: { bg: "rgba(245, 158, 11, 0.15)", text: "#d97706" },
                      ACCEPTED: { bg: "rgba(59, 130, 246, 0.15)", text: "#2563eb" },
                      IN_PROGRESS: { bg: "rgba(168, 85, 247, 0.15)", text: "#9333ea" },
                      COMPLETED: { bg: "rgba(16, 185, 129, 0.15)", text: "#059669" },
                    };
                    const sc = statusColorMap[b.status] || { bg: "hsl(var(--muted)/0.5)", text: "hsl(var(--foreground))" };

                    return (
                      <div
                        key={b.id}
                        style={{
                          padding: "14px 16px",
                          borderRadius: 12,
                          background: "hsl(var(--muted)/0.25)",
                          border: "1px solid hsl(var(--border)/0.6)",
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          flexWrap: "wrap",
                          gap: 12
                        }}
                      >
                        <div>
                          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <span style={{ fontWeight: 800, fontSize: 14, color: "hsl(var(--foreground))" }}>
                              {b.service?.name || "Service Order"}
                            </span>
                            <span style={{ fontFamily: "monospace", fontSize: 11, background: "hsl(var(--muted)/0.6)", padding: "2px 6px", borderRadius: 4 }}>
                              #{shortId}
                            </span>
                          </div>
                          <div style={{ fontSize: 12, color: "hsl(var(--muted-foreground))", marginTop: 3 }}>
                            Customer: <strong>{b.customer?.user?.name || "Customer"}</strong> &bull; Scholar: <strong>{b.scholar?.user?.name || "Pending Dispatch"}</strong>
                          </div>
                          <div style={{ fontSize: 11, color: "hsl(var(--muted-foreground))", marginTop: 2 }}>
                            Scheduled for: {b.scheduledAt ? new Date(b.scheduledAt).toLocaleString("en-IN", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : "Immediate"}
                          </div>
                        </div>

                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          <span style={{ padding: "3px 10px", borderRadius: 12, fontSize: 11, fontWeight: 700, background: sc.bg, color: sc.text }}>
                            {b.status}
                          </span>
                          <Button
                            onClick={() => setSelectedDispatchBooking(b)}
                            style={{ height: 32, fontSize: 12, fontWeight: 600, background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", color: "hsl(var(--foreground))" }}
                          >
                            Assist / Contact
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                  {(!stats?.recentLiveBookings || stats?.recentLiveBookings?.length === 0) && (
                    <div style={{ color: "hsl(var(--muted-foreground))", textAlign: "center", padding: 24, fontSize: 13, display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
                      <CheckCircle2 size={16} color="#10b981" /> No active live dispatch bookings in progress.
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

          </div>

          {/* Right Column: Operations Quick Tools & Telemetry */}
          <div style={{ display: "grid", gap: 24, alignContent: "start" }}>

            {/* 3. Operational Quick Actions */}
            <Card style={{ borderRadius: 16, border: "1px solid hsl(var(--border))" }}>
              <CardHeader style={{ paddingBottom: 8 }}>
                <CardTitle style={{ fontSize: 16, fontWeight: 700 }}>Operational Quick Navigators</CardTitle>
              </CardHeader>
              <CardContent style={{ display: "grid", gap: 10 }}>
                <button
                  onClick={() => navigate("/scholars")}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "12px 14px",
                    background: "hsl(var(--card))",
                    border: "1px solid hsl(var(--border))",
                    borderRadius: 10,
                    cursor: "pointer",
                    textAlign: "left"
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <div style={{ width: 32, height: 32, borderRadius: 8, background: "rgba(245,158,11,0.1)", display: "flex", alignItems: "center", justifyContent: "center", color: "#f59e0b" }}>
                      <GraduationCap size={16} />
                    </div>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: 13, color: "hsl(var(--foreground))" }}>Scholar Onboarding Hub</div>
                      <div style={{ fontSize: 11, color: "hsl(var(--muted-foreground))" }}>Verify profiles, credentials & approvals</div>
                    </div>
                  </div>
                  <ChevronRight size={16} style={{ color: "hsl(var(--muted-foreground))" }} />
                </button>

                <button
                  onClick={() => navigate("/bookings")}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "12px 14px",
                    background: "hsl(var(--card))",
                    border: "1px solid hsl(var(--border))",
                    borderRadius: 10,
                    cursor: "pointer",
                    textAlign: "left"
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <div style={{ width: 32, height: 32, borderRadius: 8, background: "rgba(14,165,233,0.1)", display: "flex", alignItems: "center", justifyContent: "center", color: "#0ea5e9" }}>
                      <CalendarCheck size={16} />
                    </div>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: 13, color: "hsl(var(--foreground))" }}>Booking Dispatch Center</div>
                      <div style={{ fontSize: 11, color: "hsl(var(--muted-foreground))" }}>Track dispatches, fares & assignments</div>
                    </div>
                  </div>
                  <ChevronRight size={16} style={{ color: "hsl(var(--muted-foreground))" }} />
                </button>

                <button
                  onClick={() => navigate("/support")}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "12px 14px",
                    background: "hsl(var(--card))",
                    border: "1px solid hsl(var(--border))",
                    borderRadius: 10,
                    cursor: "pointer",
                    textAlign: "left"
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <div style={{ width: 32, height: 32, borderRadius: 8, background: "rgba(37,99,235,0.1)", display: "flex", alignItems: "center", justifyContent: "center", color: "#2563eb" }}>
                      <MessageSquare size={16} />
                    </div>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: 13, color: "hsl(var(--foreground))" }}>Customer & Order Assist</div>
                      <div style={{ fontSize: 11, color: "hsl(var(--muted-foreground))" }}>Support tickets, transfers & live chat</div>
                    </div>
                  </div>
                  <ChevronRight size={16} style={{ color: "hsl(var(--muted-foreground))" }} />
                </button>

                <button
                  onClick={() => navigate("/cancellation-policy")}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "12px 14px",
                    background: "hsl(var(--card))",
                    border: "1px solid hsl(var(--border))",
                    borderRadius: 10,
                    cursor: "pointer",
                    textAlign: "left"
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <div style={{ width: 32, height: 32, borderRadius: 8, background: "rgba(5,150,105,0.1)", display: "flex", alignItems: "center", justifyContent: "center", color: "#059669" }}>
                      <Shield size={16} />
                    </div>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: 13, color: "hsl(var(--foreground))" }}>Cancellation Policy Manager</div>
                      <div style={{ fontSize: 11, color: "hsl(var(--muted-foreground))" }}>Manage refunds & cancellation rules</div>
                    </div>
                  </div>
                  <ChevronRight size={16} style={{ color: "hsl(var(--muted-foreground))" }} />
                </button>
              </CardContent>
            </Card>

            {/* 4. Operational SLA & Fulfillment Performance */}
            <Card style={{ borderRadius: 16, border: "1px solid hsl(var(--border))" }}>
              <CardHeader style={{ paddingBottom: 8 }}>
                <CardTitle style={{ fontSize: 16, fontWeight: 700 }}>Operational SLA & Health Summary</CardTitle>
              </CardHeader>
              <CardContent style={{ display: "grid", gap: 14 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 14px", background: "rgba(16, 185, 129, 0.08)", borderRadius: 10, border: "1px solid rgba(16, 185, 129, 0.2)" }}>
                  <span style={{ fontSize: 13, fontWeight: 600, color: "hsl(var(--foreground))" }}>Fulfillment SLA Compliance</span>
                  <span style={{ fontSize: 16, fontWeight: 800, color: "#10b981" }}>99.2%</span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 14px", background: "rgba(59, 130, 246, 0.08)", borderRadius: 10, border: "1px solid rgba(59, 130, 246, 0.2)" }}>
                  <span style={{ fontSize: 13, fontWeight: 600, color: "hsl(var(--foreground))" }}>Scholar Onboarding SLA</span>
                  <span style={{ fontSize: 14, fontWeight: 800, color: "#2563eb" }}>&lt; 24 Hours</span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 14px", background: "hsl(var(--muted)/0.3)", borderRadius: 10, border: "1px solid hsl(var(--border))" }}>
                  <span style={{ fontSize: 13, fontWeight: 600, color: "hsl(var(--foreground))" }}>Avg Order Assist Response</span>
                  <span style={{ fontSize: 14, fontWeight: 800, color: "hsl(var(--primary))" }}>&lt; 10 mins</span>
                </div>
              </CardContent>
            </Card>

          </div>
        </div>

        {/* Order Assist & Direct Contact Modal */}
        {selectedDispatchBooking && (
          <div style={{
            position: "fixed",
            top: 0, left: 0, right: 0, bottom: 0,
            background: "rgba(0, 0, 0, 0.65)",
            backdropFilter: "blur(4px)",
            display: "grid",
            placeItems: "center",
            zIndex: 10000,
            padding: 16
          }}>
            <div style={{
              background: "hsl(var(--card))",
              borderRadius: 20,
              padding: 24,
              width: 620,
              maxWidth: "95%",
              boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.35)",
              border: "1px solid hsl(var(--border))",
              display: "grid",
              gap: 20,
              maxHeight: "90vh",
              overflowY: "auto"
            }}>
              {/* Header */}
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                <div>
                  <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                    <h2 style={{ fontSize: 19, fontWeight: 800, margin: 0, color: "hsl(var(--foreground))" }}>
                      Order Assist & Direct Contact
                    </h2>
                    <span style={{
                      fontFamily: "monospace",
                      fontSize: 12,
                      fontWeight: 700,
                      padding: "2px 8px",
                      borderRadius: 6,
                      background: "hsl(var(--primary)/0.15)",
                      color: "hsl(var(--primary))"
                    }}>
                      #{selectedDispatchBooking.id.slice(0, 8).toUpperCase()}
                    </span>
                  </div>
                  <p style={{ margin: "4px 0 0", fontSize: 13, color: "hsl(var(--muted-foreground))" }}>
                    Real-time contact details for customer & assigned scholar to assist live delivery.
                  </p>
                </div>
                <button
                  onClick={() => setSelectedDispatchBooking(null)}
                  style={{
                    background: "none",
                    border: "none",
                    fontSize: 20,
                    cursor: "pointer",
                    color: "hsl(var(--muted-foreground))",
                    padding: 4
                  }}
                >
                  <X size={20} />
                </button>
              </div>

              {/* Booking Summary Banner */}
              <div style={{
                padding: "14px 16px",
                borderRadius: 12,
                background: "hsl(var(--muted)/0.3)",
                border: "1px solid hsl(var(--border))",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexWrap: "wrap",
                gap: 12
              }}>
                <div>
                  <div style={{ fontSize: 15, fontWeight: 700, color: "hsl(var(--foreground))" }}>
                    {selectedDispatchBooking.service?.name || "Religious Service"}
                  </div>
                  <div style={{ fontSize: 12, color: "hsl(var(--muted-foreground))", marginTop: 2 }}>
                    Scheduled: <strong>{selectedDispatchBooking.scheduledAt ? new Date(selectedDispatchBooking.scheduledAt).toLocaleString("en-IN", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : "Immediate"}</strong>
                  </div>
                </div>
                <div style={{ textAlign: "right", display: "flex", alignItems: "center", gap: 10 }}>
                  {selectedDispatchBooking.price > 0 && (
                    <span style={{ fontSize: 15, fontWeight: 800, color: "#10b981" }}>
                      ₹{selectedDispatchBooking.price.toLocaleString("en-IN")}
                    </span>
                  )}
                  <span style={{
                    padding: "4px 10px",
                    borderRadius: 12,
                    fontSize: 11,
                    fontWeight: 700,
                    background: selectedDispatchBooking.status === "IN_PROGRESS" ? "rgba(168, 85, 247, 0.15)" : "rgba(59, 130, 246, 0.15)",
                    color: selectedDispatchBooking.status === "IN_PROGRESS" ? "#9333ea" : "#2563eb"
                  }}>
                    {selectedDispatchBooking.status}
                  </span>
                </div>
              </div>

              {/* Customer & Scholar Contact Cards Grid */}
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 16 }}>
                
                {/* Customer Card */}
                <div style={{
                  padding: 16,
                  borderRadius: 14,
                  background: "hsl(var(--card))",
                  border: "1px solid hsl(var(--border))",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
                  display: "grid",
                  gap: 12
                }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, borderBottom: "1px solid hsl(var(--border)/0.6)", paddingBottom: 8 }}>
                    <User size={18} style={{ color: "#2563eb" }} />
                    <h3 style={{ fontSize: 14, fontWeight: 700, margin: 0, color: "hsl(var(--foreground))" }}>Customer Contact</h3>
                  </div>

                  <div>
                    <div style={{ fontSize: 11, color: "hsl(var(--muted-foreground))", textTransform: "uppercase", fontWeight: 600 }}>Full Name</div>
                    <div style={{ fontSize: 14, fontWeight: 700, color: "hsl(var(--foreground))", marginTop: 2 }}>
                      {selectedDispatchBooking.customer?.user?.name || "Customer Name Unavailable"}
                    </div>
                  </div>

                  <div>
                    <div style={{ fontSize: 11, color: "hsl(var(--muted-foreground))", textTransform: "uppercase", fontWeight: 600 }}>Phone Number</div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: "hsl(var(--foreground))", marginTop: 2, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                      {selectedDispatchBooking.customer?.user?.phone ? (
                        <>
                          <span>{selectedDispatchBooking.customer.user.phone}</span>
                          <a
                            href={`tel:${selectedDispatchBooking.customer.user.phone}`}
                            style={{ padding: "4px 10px", borderRadius: 6, background: "rgba(37, 99, 235, 0.12)", color: "#2563eb", fontSize: 11, fontWeight: 700, textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 4 }}
                          >
                            <Phone size={12} /> Call
                          </a>
                          <button
                            onClick={() => handleCopy(selectedDispatchBooking.customer?.user?.phone, "custPhone")}
                            style={{ padding: "4px 8px", borderRadius: 6, background: "hsl(var(--muted)/0.4)", border: "none", cursor: "pointer", fontSize: 11, fontWeight: 600 }}
                          >
                            {copiedText === "custPhone" ? "Copied!" : "Copy"}
                          </button>
                        </>
                      ) : (
                        <span style={{ color: "hsl(var(--muted-foreground))", fontStyle: "italic" }}>Not provided</span>
                      )}
                    </div>
                  </div>

                  <div>
                    <div style={{ fontSize: 11, color: "hsl(var(--muted-foreground))", textTransform: "uppercase", fontWeight: 600 }}>Email Address</div>
                    <div style={{ fontSize: 12, fontWeight: 600, color: "hsl(var(--foreground))", marginTop: 2, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", wordBreak: "break-all" }}>
                      {selectedDispatchBooking.customer?.user?.email ? (
                        <>
                          <span>{selectedDispatchBooking.customer.user.email}</span>
                          <a
                            href={`mailto:${selectedDispatchBooking.customer.user.email}`}
                            style={{ padding: "4px 10px", borderRadius: 6, background: "rgba(16, 185, 129, 0.12)", color: "#059669", fontSize: 11, fontWeight: 700, textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 4 }}
                          >
                            <Mail size={12} /> Email
                          </a>
                        </>
                      ) : (
                        <span style={{ color: "hsl(var(--muted-foreground))", fontStyle: "italic" }}>Not provided</span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Scholar Card */}
                <div style={{
                  padding: 16,
                  borderRadius: 14,
                  background: "hsl(var(--card))",
                  border: "1px solid hsl(var(--border))",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
                  display: "grid",
                  gap: 12
                }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, borderBottom: "1px solid hsl(var(--border)/0.6)", paddingBottom: 8 }}>
                    <Sparkles size={18} style={{ color: "#eab308" }} />
                    <h3 style={{ fontSize: 14, fontWeight: 700, margin: 0, color: "hsl(var(--foreground))" }}>Scholar Contact</h3>
                  </div>

                  <div>
                    <div style={{ fontSize: 11, color: "hsl(var(--muted-foreground))", textTransform: "uppercase", fontWeight: 600 }}>Full Name & Religion</div>
                    <div style={{ fontSize: 14, fontWeight: 700, color: "hsl(var(--foreground))", marginTop: 2 }}>
                      {selectedDispatchBooking.scholar?.user?.name || "Unassigned / Pending Acceptance"}
                    </div>
                    {selectedDispatchBooking.scholar?.religion && (
                      <span style={{ fontSize: 10, fontWeight: 700, padding: "2px 6px", borderRadius: 4, background: "rgba(234, 179, 8, 0.15)", color: "#ca8a04", marginTop: 3, display: "inline-block" }}>
                        {selectedDispatchBooking.scholar.religion} Scholar
                      </span>
                    )}
                  </div>

                  <div>
                    <div style={{ fontSize: 11, color: "hsl(var(--muted-foreground))", textTransform: "uppercase", fontWeight: 600 }}>Phone Number</div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: "hsl(var(--foreground))", marginTop: 2, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                      {selectedDispatchBooking.scholar?.user?.phone ? (
                        <>
                          <span>{selectedDispatchBooking.scholar.user.phone}</span>
                          <a
                            href={`tel:${selectedDispatchBooking.scholar.user.phone}`}
                            style={{ padding: "4px 10px", borderRadius: 6, background: "rgba(37, 99, 235, 0.12)", color: "#2563eb", fontSize: 11, fontWeight: 700, textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 4 }}
                          >
                            <Phone size={12} /> Call
                          </a>
                          <button
                            onClick={() => handleCopy(selectedDispatchBooking.scholar?.user?.phone, "schPhone")}
                            style={{ padding: "4px 8px", borderRadius: 6, background: "hsl(var(--muted)/0.4)", border: "none", cursor: "pointer", fontSize: 11, fontWeight: 600 }}
                          >
                            {copiedText === "schPhone" ? "Copied!" : "Copy"}
                          </button>
                        </>
                      ) : (
                        <span style={{ color: "hsl(var(--muted-foreground))", fontStyle: "italic" }}>Not provided</span>
                      )}
                    </div>
                  </div>

                  <div>
                    <div style={{ fontSize: 11, color: "hsl(var(--muted-foreground))", textTransform: "uppercase", fontWeight: 600 }}>Email Address</div>
                    <div style={{ fontSize: 12, fontWeight: 600, color: "hsl(var(--foreground))", marginTop: 2, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", wordBreak: "break-all" }}>
                      {selectedDispatchBooking.scholar?.user?.email ? (
                        <>
                          <span>{selectedDispatchBooking.scholar.user.email}</span>
                          <a
                            href={`mailto:${selectedDispatchBooking.scholar.user.email}`}
                            style={{ padding: "4px 10px", borderRadius: 6, background: "rgba(16, 185, 129, 0.12)", color: "#059669", fontSize: 11, fontWeight: 700, textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 4 }}
                          >
                            <Mail size={12} /> Email
                          </a>
                        </>
                      ) : (
                        <span style={{ color: "hsl(var(--muted-foreground))", fontStyle: "italic" }}>Not provided</span>
                      )}
                    </div>
                  </div>
                </div>

              </div>

              {/* Modal Actions Footer */}
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", paddingTop: 12, borderTop: "1px solid hsl(var(--border))", flexWrap: "wrap", gap: 12 }}>
                <Button
                  onClick={() => {
                    const shortId = selectedDispatchBooking.id.slice(0, 8);
                    setSelectedDispatchBooking(null);
                    navigate(`/messages/${shortId}`);
                  }}
                  style={{ display: "flex", alignItems: "center", gap: 8, fontWeight: 700 }}
                >
                  <MessageSquare size={16} /> Open Ticket Chat Room
                </Button>
                <Button variant="secondary" onClick={() => setSelectedDispatchBooking(null)}>
                  Close
                </Button>
              </div>

            </div>
          </div>
        )}
      </div>
    );
  }

  // ── 4. MARKETING DASHBOARD ────────────────────────────────────────
  if (stats?.department === "MARKETING") {
    return (
      <div style={{ display: "grid", gap: 24 }}>
        <section style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 16 }}>
          <div>
            <h1 style={{ fontSize: "2rem", margin: 0 }}>Marketing Dashboard</h1>
            <p style={{ margin: "8px 0 0", color: "hsl(var(--muted-foreground))" }}>
              Manage banner advertisements, track impressions/clicks, and view active campaigns.
            </p>
          </div>
          <Button onClick={() => navigate("/ads")}>Manage Campaigns</Button>
        </section>

        <section style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 20 }}>
          <StatCard title="Total Campaigns" value={stats.totalAds?.toLocaleString() || "0"} />
          <StatCard title="Active Ads" value={stats.activeAds?.toLocaleString() || "0"} trend="Active" trendUp />
          <StatCard title="Total Impressions" value={stats.totalImpressions?.toLocaleString() || "0"} />
          <StatCard title="Total Clicks" value={stats.totalClicks?.toLocaleString() || "0"} />
        </section>

        <section style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(340px, 1fr))", gap: 24 }}>
          {/* Campaigns CTR Tracking */}
          <Card style={{ gridColumn: "span 2" }}>
            <CardHeader><CardTitle>Campaign Performance & CTR Tracker</CardTitle></CardHeader>
            <CardContent>
              <div style={{ display: "grid", gap: 12 }}>
                {stats.campaigns?.map((c: any) => {
                  const ctr = c.impressions > 0 ? ((c.clicks / c.impressions) * 100).toFixed(2) : "0.00";
                  return (
                    <div key={c.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 12px", background: "hsl(var(--muted)/0.3)", borderRadius: 8 }}>
                      <div>
                        <div style={{ fontWeight: 600 }}>{c.name}</div>
                        <div style={{ fontSize: 11, color: "hsl(var(--muted-foreground))" }}>Status: {c.status}</div>
                      </div>
                      <div style={{ textAlign: "right" }}>
                        <div style={{ fontWeight: 700, color: "hsl(var(--primary))" }}>{ctr}% CTR</div>
                        <div style={{ fontSize: 11, color: "hsl(var(--muted-foreground))" }}>{c.clicks} clicks / {c.impressions} imps</div>
                      </div>
                    </div>
                  );
                })}
                {(!stats.campaigns || stats.campaigns.length === 0) && (
                  <div style={{ color: "hsl(var(--muted-foreground))", textAlign: "center" }}>No banner campaigns active.</div>
                )}
              </div>
            </CardContent>
          </Card>
        </section>
      </div>
    );
  }

  // ── 5. GLOBAL SUPER ADMIN DASHBOARD (DEFAULT) ─────────────────────
  const trendPct = (curr: number, prev: number) =>
    prev === 0 ? (curr > 0 ? 100 : 0) : parseFloat(((curr - prev) / prev * 100).toFixed(1));

  const revTrend  = trendPct(stats?.periodCurrent?.revenue || 0, stats?.previousPeriod?.totalRevenue || 0);
  const userTrend = trendPct(stats?.periodCurrent?.users || 0, stats?.previousPeriod?.totalUsers || 0);
  const schTrend  = trendPct(stats?.periodCurrent?.scholars || 0, stats?.previousPeriod?.totalScholars || 0);
  const bkgTrend  = trendPct(stats?.periodCurrent?.bookings || 0, stats?.previousPeriod?.totalBookings || 0);

  const completionRate = (stats?.totalBookings || 0) > 0
    ? ((stats?.completedBookings || 0) / stats.totalBookings * 100).toFixed(1)
    : "0.0";
  const cancellationRate = (stats?.totalBookings || 0) > 0
    ? ((stats?.cancelledBookings || 0) / stats.totalBookings * 100).toFixed(1)
    : "0.0";
  const avgOrderValue = (stats?.totalBookings || 0) > 0
    ? Math.round((stats?.totalRevenue || 0) / stats.totalBookings)
    : 0;

  const kpis = [
    {
      icon: IndianRupee,
      label: "Total Revenue",
      value: `₹${(stats?.totalRevenue || 0).toLocaleString("en-IN")}`,
      trend: revTrend,
      trendUp: revTrend >= 0,
      sub: `Period: ₹${(stats?.periodCurrent?.revenue || 0).toLocaleString("en-IN")}`,
      accent: "#6366f1",
      bg: "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)",
    },
    {
      icon: CalendarCheck,
      label: "Total Bookings",
      value: (stats?.totalBookings || 0).toLocaleString(),
      trend: bkgTrend,
      trendUp: bkgTrend >= 0,
      sub: `Active: ${(stats?.activeBookings || stats?.periodCurrent?.bookings || 0).toLocaleString()}`,
      accent: "#0ea5e9",
      bg: "linear-gradient(135deg, #0ea5e9 0%, #38bdf8 100%)",
    },
    {
      icon: GraduationCap,
      label: "Total Scholars",
      value: (stats?.totalScholars || 0).toLocaleString(),
      trend: schTrend,
      trendUp: schTrend >= 0,
      sub: `Pending: ${(stats?.pendingScholars || 0).toLocaleString()}`,
      accent: "#f59e0b",
      bg: "linear-gradient(135deg, #f59e0b 0%, #fbbf24 100%)",
    },
    {
      icon: Users,
      label: "Total Customers",
      value: (stats?.totalCustomers ?? stats?.totalUsers ?? 0).toLocaleString(),
      trend: userTrend,
      trendUp: userTrend >= 0,
      sub: `Repeat: ${(stats?.repeatCustomers || 0).toLocaleString()}`,
      accent: "#10b981",
      bg: "linear-gradient(135deg, #10b981 0%, #34d399 100%)",
    },
  ];

  return (
    <div style={{ display: "grid", gap: 28 }}>
      {/* ── HERO HEADER ──────────────────────────────── */}
      <style>{`
        @keyframes fadeSlideUp { from{opacity:0;transform:translateY(16px)} to{opacity:1;transform:translateY(0)} }
        .kpi-card:hover { transform: translateY(-3px) !important; box-shadow: 0 16px 32px rgba(0,0,0,0.08) !important; }
        .kpi-card { transition: transform .2s ease, box-shadow .2s ease; }
        .sec-card:hover { border-color: rgba(99,102,241,.35) !important; }
        .sec-card { transition: border-color .2s ease; }
      `}</style>

      <section style={{
        display: "flex", justifyContent: "space-between", alignItems: "center",
        flexWrap: "wrap", gap: 16,
        padding: "24px 28px",
        background: "linear-gradient(135deg, hsl(var(--card)) 0%, hsl(var(--muted)/0.18) 100%)",
        borderRadius: 20, border: "1px solid hsl(var(--border))",
        animation: "fadeSlideUp .4s ease",
      }}>
        <div>
          <div style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            padding: "4px 10px",
            borderRadius: 20,
            background: "rgba(16, 185, 129, 0.1)",
            border: "1px solid rgba(16, 185, 129, 0.25)",
            color: "#059669",
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: 0.5,
            marginBottom: 8,
          }}>
            <Activity size={13} color="#059669" />
            <span>Real-Time Telemetry</span>
          </div>
          <h1 style={{ fontSize: "1.85rem", fontWeight: 800, margin: 0, color: "hsl(var(--foreground))", letterSpacing: -0.5 }}>
            Platform Overview
          </h1>
          <p style={{ margin: "6px 0 0", color: "hsl(var(--muted-foreground))", lineHeight: 1.6, fontSize: 13 }}>
            Real-time analytics across revenue, scholars, customers and bookings — {stats?.period?.label || "30 days"}.
          </p>
        </div>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
          <button
            onClick={() => navigate('/scholars')}
            style={{
              padding: "9px 18px",
              borderRadius: 10,
              border: "1px solid hsl(var(--border))",
              background: "hsl(var(--card))",
              color: "hsl(var(--foreground))",
              fontWeight: 600,
              fontSize: 13,
              cursor: "pointer",
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
            }}>
            <UserCheck size={15} color="#475569" />
            Scholar Approvals {(stats?.pendingScholars || 0) > 0 && (
              <span style={{ background: "#ef4444", color: "#fff", borderRadius: 12, padding: "1px 7px", fontSize: 11, fontWeight: 700 }}>
                {stats?.pendingScholars}
              </span>
            )}
          </button>
          <button
            onClick={() => navigate('/reports')}
            style={{
              padding: "9px 20px",
              borderRadius: 10,
              border: "none",
              background: "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)",
              color: "#fff",
              fontWeight: 700,
              fontSize: 13,
              cursor: "pointer",
              boxShadow: "0 4px 14px rgba(99,102,241,.25)",
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
            }}>
            <FileBarChart2 size={15} />
            Generate Report
          </button>
        </div>
      </section>

      {/* ── KPI CARDS ────────────────────────────────── */}
      <section style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))", gap: 18 }}>
        {kpis.map((k) => {
          const Icon = k.icon;
          return (
            <div key={k.label} className="kpi-card" style={{
              borderRadius: 18, overflow: "hidden",
              background: "hsl(var(--card))",
              border: "1px solid hsl(var(--border))",
              boxShadow: "0 4px 16px rgba(0,0,0,0.04)",
            }}>
              {/* Accent bar */}
              <div style={{ height: 4, background: k.bg }} />
              <div style={{ padding: "20px 22px" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                  <div>
                    <div style={{ fontSize: 11, fontWeight: 700, color: "hsl(var(--muted-foreground))", textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 6 }}>
                      {k.label}
                    </div>
                    <div style={{ fontSize: 26, fontWeight: 800, color: "hsl(var(--foreground))", lineHeight: 1 }}>
                      {loading ? <span style={{ color: "#94a3b8" }}>—</span> : k.value}
                    </div>
                  </div>
                  <div style={{
                    width: 44, height: 44, borderRadius: 12,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    background: `${k.accent}14`,
                    border: `1px solid ${k.accent}25`,
                    color: k.accent,
                  }}>
                    <Icon size={22} />
                  </div>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 14 }}>
                  <span style={{ fontSize: 12, color: "hsl(var(--muted-foreground))" }}>{k.sub}</span>
                  <span style={{
                    fontSize: 11, fontWeight: 700, padding: "3px 8px", borderRadius: 20,
                    background: k.trendUp ? "#ecfdf5" : "#fef2f2",
                    color: k.trendUp ? "#059669" : "#dc2626",
                    border: `1px solid ${k.trendUp ? "#a7f3d0" : "#fecaca"}`,
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 3,
                  }}>
                    {k.trendUp ? <TrendingUp size={12} /> : <TrendingDown size={12} />} {Math.abs(k.trend)}%
                  </span>
                </div>
              </div>
            </div>
          );
        })}
      </section>

      {/* ── SECONDARY METRICS ────────────────────────── */}
      <section style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 16 }}>
        {[
          { label: "Avg. Order Value", value: `₹${avgOrderValue.toLocaleString("en-IN")}`, icon: Receipt, color: "#6366f1", bg: "rgba(99,102,241,0.1)", border: "rgba(99,102,241,0.2)" },
          { label: "Completion Rate", value: `${completionRate}%`, icon: CheckCircle2, color: "#10b981", bg: "rgba(16,185,129,0.1)", border: "rgba(16,185,129,0.2)", bar: parseFloat(completionRate) },
          { label: "Cancellation Rate", value: `${cancellationRate}%`, icon: XCircle, color: "#ef4444", bg: "rgba(239,68,68,0.1)", border: "rgba(239,68,68,0.2)", bar: parseFloat(cancellationRate) },
          { label: "Scholar Utilization", value: `${stats?.scholarUtilization ?? 0}%`, icon: Briefcase, color: "#f59e0b", bg: "rgba(245,158,11,0.1)", border: "rgba(245,158,11,0.2)", bar: stats?.scholarUtilization ?? 0 },
          { label: "Repeat Booking Rate", value: `${stats?.repeatBookingRate ?? 0}%`, icon: RotateCcw, color: "#0ea5e9", bg: "rgba(14,165,233,0.1)", border: "rgba(14,165,233,0.2)", bar: stats?.repeatBookingRate ?? 0 },
        ].map((m) => {
          const Icon = m.icon;
          return (
            <div key={m.label} className="sec-card" style={{
              padding: "16px 20px", borderRadius: 14, background: "hsl(var(--card))",
              border: "1px solid hsl(var(--border))", boxShadow: "0 2px 8px rgba(0,0,0,.03)",
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
                <div style={{
                  width: 28, height: 28, borderRadius: 8,
                  backgroundColor: m.bg,
                  border: `1px solid ${m.border}`,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  color: m.color,
                }}>
                  <Icon size={14} />
                </div>
                <span style={{ fontSize: 11, fontWeight: 700, color: "hsl(var(--muted-foreground))", textTransform: "uppercase", letterSpacing: 0.6 }}>
                  {m.label}
                </span>
              </div>
              <div style={{ fontSize: 22, fontWeight: 800, color: m.color, marginBottom: m.bar !== undefined ? 10 : 0 }}>
                {m.value}
              </div>
              {m.bar !== undefined && (
                <div style={{ height: 6, borderRadius: 3, background: "hsl(var(--muted)/0.4)", overflow: "hidden" }}>
                  <div style={{ width: `${Math.min(m.bar, 100)}%`, height: "100%", background: m.color, borderRadius: 3, transition: "width 1s ease" }} />
                </div>
              )}
            </div>
          );
        })}
      </section>

      {/* ── CHARTS ROW ───────────────────────────────── */}
      <section style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(340px, 1fr))", gap: 24 }}>
        <div style={{ borderRadius: 18, background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", padding: 24, boxShadow: "0 4px 16px rgba(0,0,0,.05)" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20, flexWrap: "wrap", gap: 10 }}>
            <div>
              <div style={{ fontSize: 16, fontWeight: 800, color: "hsl(var(--foreground))" }}>Revenue Growth</div>
              <div style={{ fontSize: 12, color: "hsl(var(--muted-foreground))", marginTop: 2 }}>Trend over selected period</div>
            </div>
            <ChartFilterBar value={revPeriod} onChange={setRevPeriod} />
          </div>
          <div style={{ height: 320 }}>
            <RevenueChart period={revPeriod} showFilter={false} height={310} />
          </div>
        </div>

        <div style={{ borderRadius: 18, background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", padding: 24, boxShadow: "0 4px 16px rgba(0,0,0,.05)" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16, flexWrap: "wrap", gap: 10 }}>
            <div>
              <div style={{ fontSize: 16, fontWeight: 800, color: "hsl(var(--foreground))" }}>Religion Distribution</div>
              <div style={{ fontSize: 12, color: "hsl(var(--muted-foreground))", marginTop: 2 }}>Cross-ecosystem breakdown across scholars & users</div>
            </div>
            <ChartFilterBar value={piePeriod} onChange={setPiePeriod} />
          </div>
          <div style={{ height: 320 }}>
            <PieChartBox period={piePeriod} />
          </div>
        </div>
      </section>

      {/* ── RECENT ACTIVITY ──────────────────────────── */}
      <div style={{ borderRadius: 18, background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", overflow: "hidden", boxShadow: "0 4px 16px rgba(0,0,0,.05)" }}>
        <div style={{ padding: "20px 24px", borderBottom: "1px solid hsl(var(--border))", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 800, color: "hsl(var(--foreground))" }}>Recent Activity</div>
            <div style={{ fontSize: 12, color: "hsl(var(--muted-foreground))", marginTop: 2 }}>Latest bookings, approvals, and system events</div>
          </div>
          <button
            onClick={() => navigate('/bookings')}
            style={{ fontSize: 12, fontWeight: 700, color: "#6366f1", background: "rgba(99,102,241,.1)", border: "none", padding: "6px 14px", borderRadius: 8, cursor: "pointer" }}>
            View All →
          </button>
        </div>
        <div style={{ padding: 24 }}>
          <Dashboard1 />
        </div>
      </div>
    </div>
  );
};

export default Dashboard;

